import React, { useState, useEffect } from 'react';
import ListLoadFailed from './ui/ListLoadFailed';
import systemDataService from '../services/systemDataService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileUpload, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import BranchStaffingModal from './BranchStaffingModal';
import PortBulkUploadModal from './PortBulkUploadModal';
import AmountInput from './ui/AmountInput';
import './SystemDataManagement.css';

const SystemDataManagement = ({ activeTab }) => {
  const { showSuccess, showError } = useFlashMessage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'delete'
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [crmProfiles, setCrmProfiles] = useState([]);
  const [hmsEntities, setHmsEntities] = useState([]);
  // Row 128: the company list failed to load (never shown as "Not linked").
  const [hmsEntitiesLoadFailed, setHmsEntitiesLoadFailed] = useState(false);
  
  // Dropdown state (matching employee tables)
  const [activeDropdown, setActiveDropdown] = useState(null);

  // VAT Rates modal state
  const [showVatModal, setShowVatModal] = useState(false);
  const [vatModalOffice, setVatModalOffice] = useState(null);
  const [vatRates, setVatRates] = useState([]);
  const [vatEntityName, setVatEntityName] = useState('');
  const [vatLoading, setVatLoading] = useState(false);
  const [vatSaving, setVatSaving] = useState(false);
  const [vatError, setVatError] = useState(null);
  const [showAddVatForm, setShowAddVatForm] = useState(false);
  const [newVatData, setNewVatData] = useState({ vat_code: '', description: '', rate_percentage: '', country_code: '' });
  const [vatCreating, setVatCreating] = useState(false);

  // Branch Staffing modal state (Sprint 1F)
  const [showStaffingModal, setShowStaffingModal] = useState(false);
  const [staffingBranch, setStaffingBranch] = useState(null);

  // Ports sub-tab state ---------------------------------------------------
  // The ports table is fully server-driven — search, filtering AND pagination
  // all run on the backend because the dataset is ~7,500+ rows and growing.
  // We load PORTS_PAGE_SIZE rows at a time; "Show more" fetches the next slice
  // via limit/offset and appends it. ``data`` only ever holds the rows loaded
  // so far, and ``portsTotal`` is the full filtered count for "Showing X of N".
  const PORTS_PAGE_SIZE = 50;
  const [showPortBulkUpload, setShowPortBulkUpload] = useState(false);
  const [portSearchInput, setPortSearchInput] = useState('');
  const [portSearchActive, setPortSearchActive] = useState('');
  // Module-list filter: '' (all) | 'bunker' | 'cargo'. Each trade module curates
  // its own sub-list off this master table; this narrows the view to one of them.
  const [portListFilter, setPortListFilter] = useState('');
  const [portsTotal, setPortsTotal] = useState(0);
  const [portsLoadingMore, setPortsLoadingMore] = useState(false);

  // The 6 fixed branch codes are seeded in fuel_enquiries/migrations/0002_seed_lookups.py
  // and locked to ``IMTBranchOffice.BRANCH_CHOICES`` on the model. Listed here so the
  // create dropdown matches the backend without a round-trip; create-only — the field
  // is read-only on edit because historical FuelEnquiry rows reference the FK.
  const BRANCH_CODE_OPTIONS = [
    { value: 'china', label: 'China' },
    { value: 'eu', label: 'EU' },
    { value: 'korea', label: 'Korea' },
    { value: 'uae', label: 'UAE' },
    { value: 'usa', label: 'USA' },
    { value: 'singapore', label: 'Singapore' },
  ];

  // Fetch data when activeTab changes (and when the active port search
  // changes — only for the ports tab; debounced via the controlled search
  // box, see handlePortSearchSubmit).
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, portSearchActive, portListFilter]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeDropdown) {
        setActiveDropdown(null);
        // Remove dropdown-open class from all dropdowns
        document.querySelectorAll('.action-dropdown').forEach(dropdown => {
          dropdown.classList.remove('dropdown-open');
        });
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Cleanup: Remove modal-open class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let result = [];
      switch (activeTab) {
        case 'offices':
          result = await systemDataService.getOffices();
          break;
        case 'departments':
          result = await systemDataService.getDepartments();
          break;
        case 'positions':
          result = await systemDataService.getPositions();
          break;
        case 'units':
          result = await systemDataService.getUnits();
          break;
        case 'branches':
          result = await systemDataService.getBranches();
          break;
        case 'ports': {
          // Always (re)load the first page — new search, tab re-entry, or a
          // post-mutation refresh all reset back to the top of the list.
          const { ports, total } = await systemDataService.getPorts({
            search: portSearchActive,
            bunkerOnly: portListFilter === 'bunker',
            cargoOnly: portListFilter === 'cargo',
            limit: PORTS_PAGE_SIZE,
            offset: 0,
          });
          setPortsTotal(total);
          result = ports;
          break;
        }
        default:
          result = [];
      }
      setData(result);
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = getErrorMessage(err, 'Settings could not be updated. Please try again.');
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
    
    // Add/remove dropdown-open class to the action-dropdown element
    setTimeout(() => {
      document.querySelectorAll('.action-dropdown').forEach(dropdown => {
        dropdown.classList.remove('dropdown-open');
      });
      
      if (activeDropdown !== dropdownId) {
        const element = document.querySelector(`[data-dropdown-id="${dropdownId}"]`);
        if (element) {
          element.classList.add('dropdown-open');
        }
      }
    }, 0);
  };

  const fetchCRMProfilesIfNeeded = async () => {
    if (activeTab === 'offices' && crmProfiles.length === 0) {
      try {
        const profiles = await systemDataService.getCRMProfilesForLinking();
        setCrmProfiles(profiles);
      } catch (err) {
        console.error('Error fetching CRM profiles for linking:', err);
      }
    }
    if (activeTab === 'offices' && hmsEntities.length === 0) {
      await loadHmsEntitiesForLinking();
    }
  };

  const loadHmsEntitiesForLinking = async () => {
    try {
      setHmsEntities(await systemDataService.getHMSEntitiesForLinking());
      setHmsEntitiesLoadFailed(false);
    } catch (err) {
      console.error('Error fetching HMS entities for linking:', err);
      setHmsEntitiesLoadFailed(true);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedItem(null);
    setFormData({});
    setShowModal(true);
    document.body.classList.add('modal-open');
    fetchCRMProfilesIfNeeded();
  };

  const handleEdit = (item) => {
    setActiveDropdown(null);
    setModalMode('edit');
    setSelectedItem(item);
    setFormData({ ...item });
    setShowModal(true);
    document.body.classList.add('modal-open');
    fetchCRMProfilesIfNeeded();
  };

  const handleDeleteClick = (item) => {
    setActiveDropdown(null); // Close dropdown
    setModalMode('delete');
    setSelectedItem(item);
    setShowModal(true);
    document.body.classList.add('modal-open');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      let result;
      const categoryName = activeTab.slice(0, -1); // Remove 's' from end
      const categoryTitle = getCategoryTitle();

      if (modalMode === 'create') {
        switch (activeTab) {
          case 'offices':
            result = await systemDataService.createOffice(formData);
            break;
          case 'departments':
            result = await systemDataService.createDepartment(formData);
            break;
          case 'positions':
            result = await systemDataService.createPosition(formData);
            break;
          case 'units':
            result = await systemDataService.createUnit(formData);
            break;
          case 'branches':
            result = await systemDataService.createBranch(formData);
            break;
          case 'ports':
            result = await systemDataService.createPort(formData);
            break;
          default:
            break;
        }
        showSuccess(`${categoryTitle} created successfully`);
      } else if (modalMode === 'edit') {
        switch (activeTab) {
          case 'offices':
            result = await systemDataService.updateOffice(selectedItem.id, formData);
            break;
          case 'departments':
            result = await systemDataService.updateDepartment(selectedItem.id, formData);
            break;
          case 'positions':
            result = await systemDataService.updatePosition(selectedItem.id, formData);
            break;
          case 'units':
            result = await systemDataService.updateUnit(selectedItem.id, formData);
            break;
          case 'branches':
            // ``code`` is immutable post-create — strip it from the patch payload defensively.
            // (The input is also disabled in the form, but this protects against any state leak.)
            // eslint-disable-next-line no-unused-vars
            const { code: _ignoredCode, ...branchPatch } = formData;
            result = await systemDataService.updateBranch(selectedItem.id, branchPatch);
            break;
          case 'ports': {
            // ``unlocode`` is immutable post-create — strip it from the patch payload.
            // eslint-disable-next-line no-unused-vars
            const { unlocode: _ignoredUnlocode, ...portPatch } = formData;
            result = await systemDataService.updatePort(selectedItem.id, portPatch);
            break;
          }
          default:
            break;
        }
        showSuccess(`${categoryTitle} updated successfully`);
      } else if (modalMode === 'delete') {
        switch (activeTab) {
          case 'offices':
            result = await systemDataService.deleteOffice(selectedItem.id);
            break;
          case 'departments':
            result = await systemDataService.deleteDepartment(selectedItem.id);
            break;
          case 'positions':
            result = await systemDataService.deletePosition(selectedItem.id);
            break;
          case 'units':
            result = await systemDataService.deleteUnit(selectedItem.id);
            break;
          case 'branches':
            result = await systemDataService.deleteBranch(selectedItem.id);
            break;
          case 'ports':
            result = await systemDataService.deletePort(selectedItem.id);
            break;
          default:
            break;
        }
        
        if (result.affected_employees > 0) {
          showSuccess(`${categoryTitle} deleted. ${result.affected_employees} employee(s) set to "Not Assigned"`);
        } else {
          showSuccess(`${categoryTitle} deleted successfully`);
        }
      }

      setShowModal(false);
      setSelectedItem(null);
      setFormData({});
      document.body.classList.remove('modal-open');
      await fetchData();
      
      // Dispatch custom event to notify other components that system data has changed
      // This allows AccountInformation and AdminEditEmployee to reload their dropdown options
      const event = new CustomEvent('systemDataUpdated', { 
        detail: { 
          category: activeTab,
          action: modalMode 
        } 
      });
      window.dispatchEvent(event);
      console.log('🔔 System data updated event dispatched:', { category: activeTab, action: modalMode });
    } catch (err) {
      console.error('Error submitting form:', err);
      const errorMessage = getErrorMessage(err, 'Settings could not be updated. Please try again.');
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData({});
    setError(null);
    document.body.classList.remove('modal-open');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVatRatesClick = async (item) => {
    setActiveDropdown(null);
    setVatModalOffice(item);
    setShowVatModal(true);
    setVatLoading(true);
    setVatError(null);
    document.body.classList.add('modal-open');

    try {
      const result = await systemDataService.getOfficeVatRates(item.id);
      setVatRates(result.vat_rates || []);
      setVatEntityName(result.entity_name || '');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to load VAT rates';
      setVatError(msg);
      setVatRates([]);
    } finally {
      setVatLoading(false);
    }
  };

  const handleVatToggle = (rateId) => {
    setVatRates(prev => prev.map(r =>
      r.id === rateId ? { ...r, is_allowed: !r.is_allowed } : r
    ));
  };

  const handleVatSave = async () => {
    setVatSaving(true);
    try {
      const allowedIds = vatRates.filter(r => r.is_allowed).map(r => r.id);
      await systemDataService.updateOfficeVatRates(vatModalOffice.id, allowedIds);
      showSuccess(`VAT rates updated for ${vatEntityName}`);
      setShowVatModal(false);
      document.body.classList.remove('modal-open');
    } catch (err) {
      const msg = getErrorMessage(err, 'Failed to save VAT rates');
      showError(msg);
    } finally {
      setVatSaving(false);
    }
  };

  const handleCreateVatRate = async () => {
    if (!newVatData.vat_code || !newVatData.description || !newVatData.rate_percentage) return;
    setVatCreating(true);
    try {
      const result = await systemDataService.createOfficeVatRate(vatModalOffice.id, newVatData);
      setVatRates(prev => [...prev, result.vat_rate].sort((a, b) => a.vat_code.localeCompare(b.vat_code)));
      setNewVatData({ vat_code: '', description: '', rate_percentage: '', country_code: '' });
      setShowAddVatForm(false);
      showSuccess(result.message);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create VAT rate';
      showError(msg);
    } finally {
      setVatCreating(false);
    }
  };

  const handleVatModalClose = () => {
    setShowVatModal(false);
    setVatModalOffice(null);
    setVatRates([]);
    setVatEntityName('');
    setVatError(null);
    setShowAddVatForm(false);
    setNewVatData({ vat_code: '', description: '', rate_percentage: '', country_code: '' });
    document.body.classList.remove('modal-open');
  };

  const getCategoryTitle = () => {
    switch (activeTab) {
      case 'offices':
        return 'Office';
      case 'departments':
        return 'Department';
      case 'positions':
        return 'Position';
      case 'units':
        return 'Unit';
      case 'branches':
        return 'IMT Branch';
      case 'ports':
        return 'Port';
      default:
        return 'Item';
    }
  };

  // Port search submit (Enter key or button) — pushes the input value into
  // ``portSearchActive`` which is what useEffect listens to. Avoids firing a
  // backend round-trip on every keystroke.
  const handlePortSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setPortSearchActive(portSearchInput.trim());
  };
  const handlePortSearchClear = () => {
    setPortSearchInput('');
    setPortSearchActive('');
  };

  // "Show more" for the ports tab — fetch the next PORTS_PAGE_SIZE slice from
  // the server (offset = rows already loaded) and append it, so payloads stay
  // small instead of shipping all ~7,500 rows at once.
  const handleLoadMorePorts = async () => {
    if (portsLoadingMore) return;
    setPortsLoadingMore(true);
    try {
      const { ports, total } = await systemDataService.getPorts({
        search: portSearchActive,
        bunkerOnly: portListFilter === 'bunker',
        cargoOnly: portListFilter === 'cargo',
        limit: PORTS_PAGE_SIZE,
        offset: data.length,
      });
      setPortsTotal(total);
      setData(prev => [...prev, ...ports]);
    } catch (err) {
      console.error('Error loading more ports:', err);
      showError(getErrorMessage(err, 'Could not load more ports. Please try again.'));
    } finally {
      setPortsLoadingMore(false);
    }
  };

  const handlePortBulkUploadClose = () => {
    setShowPortBulkUpload(false);
    document.body.classList.remove('modal-open');
  };
  const handlePortBulkUploadOpen = () => {
    setShowPortBulkUpload(true);
    document.body.classList.add('modal-open');
  };
  const handlePortBulkUploaded = async () => {
    await fetchData();
  };

  // Branch staffing modal handlers -----------------------------------------
  // Opening the modal does NOT close any other modals — branches don't share
  // the create/edit/VAT modal slot. The modal is responsible for its own
  // employee-list fetch (one fetch per open) and persistence via
  // ``systemDataService.staffBranch``.
  const handleStaffBranchClick = (branch) => {
    setActiveDropdown(null);
    setStaffingBranch(branch);
    setShowStaffingModal(true);
    document.body.classList.add('modal-open');
  };

  const handleStaffingModalClose = () => {
    setShowStaffingModal(false);
    setStaffingBranch(null);
    document.body.classList.remove('modal-open');
  };

  const handleStaffingSaved = async () => {
    handleStaffingModalClose();
    await fetchData();
  };

  return (
    <div className="system-data-container">
      <div className="system-data-header">
        {activeTab === 'ports' ? (
          <div className="ports-toolbar">
            <form onSubmit={handlePortSearchSubmit} className="ports-search-form">
              <div className="ports-search-input-wrap">
                <FontAwesomeIcon icon={faSearch} className="ports-search-icon" />
                <input
                  type="text"
                  value={portSearchInput}
                  onChange={(e) => setPortSearchInput(e.target.value)}
                  placeholder="Search port name, UN/LOCODE, or country..."
                  className="ports-search-input"
                />
                {portSearchInput && (
                  <button
                    type="button"
                    onClick={handlePortSearchClear}
                    className="ports-search-clear"
                    title="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
              <button type="submit" className="ports-search-submit">
                Search
              </button>
            </form>
            <select
              className="ports-list-filter"
              value={portListFilter}
              onChange={(e) => setPortListFilter(e.target.value)}
              title="Show only ports on a module's curated list"
              aria-label="Filter by module list"
            >
              <option value="">All ports</option>
              <option value="bunker">Bunker list only</option>
              <option value="cargo">Cargo list only</option>
            </select>
            <div className="ports-toolbar-actions">
              <button
                type="button"
                className="ports-bulk-upload-btn"
                onClick={handlePortBulkUploadOpen}
                title="Bulk upload ports from CSV"
              >
                <FontAwesomeIcon icon={faFileUpload} /> Bulk Upload
              </button>
              <button
                className="create-btn"
                onClick={handleCreate}
                title={`Add ${getCategoryTitle()}`}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>
        ) : (
          <button
            className="create-btn"
            onClick={handleCreate}
            title={`Add ${getCategoryTitle()}`}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        )}
      </div>

      {/* Employee-style table structure */}
      {loading ? (
        <div className="loading">Loading data...</div>
      ) : (
        <table className="employees-table">
          <thead>
            <tr>
              {activeTab === 'offices' && (
                <>
                  <th>Name</th>
                  <th>IMT Entity</th>
                  <th>Linked CRM Profile</th>
                  <th>Employees</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'departments' && (
                <>
                  <th>Name</th>
                  <th>Employees</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'positions' && (
                <>
                  <th>Name</th>
                  <th>Verification</th>
                  <th>Employees</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'units' && (
                <>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'branches' && (
                <>
                  <th>Branch Name</th>
                  <th>Branch Manager</th>
                  <th>Ops Team</th>
                  <th>Exec Team</th>
                  <th>Active</th>
                  <th>Actions</th>
                </>
              )}
              {activeTab === 'ports' && (
                <>
                  <th>UN/LOCODE</th>
                  <th>Port Name</th>
                  <th>Country</th>
                  <th>ISO</th>
                  <th>Bunker</th>
                  <th>Cargo</th>
                  <th>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    activeTab === 'ports'
                      ? 7
                      : activeTab === 'branches'
                      ? 6
                      : activeTab === 'offices'
                      ? 5
                      : activeTab === 'positions' ? 4 : 3
                  }
                  className="no-data"
                >
                  <i className="fas fa-inbox"></i>
                  <p>No {activeTab} found. Create one to get started.</p>
                </td>
              </tr>
            ) : activeTab === 'ports' ? (
              data.map(item => (
                <tr key={item.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.unlocode}</td>
                  <td>{item.port_name}</td>
                  <td>{item.country_name}</td>
                  <td style={{ fontFamily: 'monospace', color: '#6b7280' }}>{item.country_iso_code}</td>
                  <td>
                    {item.is_bunker_port ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>
                        <i className="fas fa-check"></i> Yes
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>No</span>
                    )}
                  </td>
                  <td>
                    {item.is_cargo_port ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>
                        <i className="fas fa-check"></i> Yes
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>No</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div
                      className="action-dropdown"
                      onClick={(e) => e.stopPropagation()}
                      data-dropdown-id={`${activeTab}-${item.id}`}
                    >
                      <button
                        className="gear-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(`${activeTab}-${item.id}`);
                        }}
                      >
                        Actions
                      </button>
                      {activeDropdown === `${activeTab}-${item.id}` && (
                        <div className="dropdown-menu">
                          <button onClick={() => handleEdit(item)}>
                            Edit
                          </button>
                          <hr />
                          <button className="danger" onClick={() => handleDeleteClick(item)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : activeTab === 'branches' ? (
              data.map(item => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    {item.branch_manager_name ? (
                      <span style={{ fontWeight: '500' }}>{item.branch_manager_name}</span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85rem' }}>
                        — Assign
                      </span>
                    )}
                  </td>
                  <td>{item.ops_team_count || 0} {(item.ops_team_count === 1) ? 'member' : 'members'}</td>
                  <td>{item.exec_team_count || 0} {(item.exec_team_count === 1) ? 'member' : 'members'}</td>
                  <td>
                    {item.is_active ? (
                      <span style={{ color: '#059669', fontWeight: '600' }}>
                        <i className="fas fa-check"></i>
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Inactive</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div
                      className="action-dropdown"
                      onClick={(e) => e.stopPropagation()}
                      data-dropdown-id={`${activeTab}-${item.id}`}
                    >
                      <button
                        className="gear-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(`${activeTab}-${item.id}`);
                        }}
                      >
                        Actions
                      </button>
                      {activeDropdown === `${activeTab}-${item.id}` && (
                        <div className="dropdown-menu">
                          <button onClick={() => handleEdit(item)}>
                            Edit
                          </button>
                          <hr />
                          <button onClick={() => handleStaffBranchClick(item)}>
                            Staff
                          </button>
                          <hr />
                          <button className="danger" onClick={() => handleDeleteClick(item)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
            data.map(item => (
              <tr key={item.id}>
                {activeTab === 'units' ? (
                  <>
                    <td>{item.code}</td>
                    <td>{item.name}</td>
                  </>
                ) : (
                  <td>{item.name}</td>
                )}
                {activeTab === 'offices' && (
                  <td>
                    {item.hms_entity_name ? (
                      <span style={{ color: '#059669', fontWeight: '500', fontSize: '0.85rem' }}>
                        {item.hms_entity_name}
                      </span>
                    ) : (
                      // Not cosmetic: staff at an unlinked office are refused
                      // Financials outright, so this row is a to-do list.
                      <span style={{ color: '#b45309', fontWeight: '600', fontSize: '0.85rem' }}>
                        Not linked — no Financials
                      </span>
                    )}
                  </td>
                )}
                {activeTab === 'offices' && (
                  <td>
                    {item.linked_crm_profile_name ? (
                      <span style={{ color: '#059669', fontWeight: '500', fontSize: '0.85rem' }}>
                        <i className="fas fa-link" style={{ marginRight: '0.3rem', fontSize: '0.7rem' }}></i>
                        {item.linked_crm_profile_name}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Not linked</span>
                    )}
                  </td>
                )}
                {activeTab === 'positions' && (
                  <td>
                    {item.requires_verification ? (
                      <span className="status-badge verification-required">
                        Yes
                      </span>
                    ) : (
                      <span className="status-badge verification-not-required">
                        No
                      </span>
                    )}
                  </td>
                )}
                {activeTab !== 'units' && (
                  <td>{item.employee_count || 0}</td>
                )}
                <td className="actions-cell">
                  <div 
                    className="action-dropdown" 
                    onClick={(e) => e.stopPropagation()} 
                    data-dropdown-id={`${activeTab}-${item.id}`}
                  >
                    <button 
                      className="gear-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(`${activeTab}-${item.id}`);
                      }}
                    >
                      Actions
                    </button>
                    {activeDropdown === `${activeTab}-${item.id}` && (
                      <div className="dropdown-menu">
                        <button onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        {activeTab === 'offices' && (
                          <>
                            <hr />
                            <button onClick={() => handleVatRatesClick(item)}>
                              VAT Rates
                            </button>
                          </>
                        )}
                        <hr />
                        <button className="danger" onClick={() => handleDeleteClick(item)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      )}

      {/* Server-side "Show more" pagination for the ports tab. ``data`` holds
          only the rows loaded so far; each click fetches the next
          PORTS_PAGE_SIZE slice from the backend (search is server-side too). */}
      {!loading && activeTab === 'ports' && data.length > 0 && (
        <div className="ports-show-more-row">
          <span className="ports-show-more-count">
            Showing <strong>{data.length}</strong> of{' '}
            <strong>{portsTotal}</strong> {portsTotal === 1 ? 'port' : 'ports'}
            {portSearchActive ? ' (filtered)' : ''}
          </span>
          {data.length < portsTotal && (
            <button
              type="button"
              className="ports-show-more-btn"
              onClick={handleLoadMorePorts}
              disabled={portsLoadingMore}
            >
              {portsLoadingMore
                ? 'Loading…'
                : `Show ${Math.min(PORTS_PAGE_SIZE, portsTotal - data.length)} more`}
            </button>
          )}
        </div>
      )}

      {/* Modal for Create/Edit/Delete */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && `Create New ${getCategoryTitle()}`}
                {modalMode === 'edit' && `Edit ${getCategoryTitle()}`}
                {modalMode === 'delete' && `Delete ${getCategoryTitle()}`}
              </h2>
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {modalMode === 'delete' ? (
                <div className="delete-confirmation">
                  <i className="fas fa-exclamation-triangle warning-icon"></i>
                  <p>
                    Are you sure you want to delete{' '}
                    <strong>
                      {activeTab === 'units'
                        ? selectedItem?.code
                        : activeTab === 'ports'
                          ? `${selectedItem?.port_name} (${selectedItem?.unlocode})`
                          : selectedItem?.name}
                    </strong>?
                  </p>
                  {activeTab === 'units' && selectedItem?.line_item_count > 0 && (
                    <div className="warning-box">
                      <i className="fas fa-info-circle"></i>
                      <p>
                        This unit is used in <strong>{selectedItem.line_item_count}</strong> line item(s).
                        <br />
                        You must reassign these line items to another unit before deleting.
                      </p>
                    </div>
                  )}
                  {activeTab === 'ports' && (
                    <div className="warning-box">
                      <i className="fas fa-info-circle"></i>
                      <p>
                        Port deletion does not cascade to FuelEnquiry / TradeRecap rows
                        that reference this port — those records will keep their stored
                        port FK but the port name will no longer be selectable for new
                        enquiries. Re-create the port to restore availability.
                      </p>
                    </div>
                  )}
                  {activeTab !== 'units' && activeTab !== 'ports' && selectedItem?.employee_count > 0 && (
                    <div className="warning-box">
                      <i className="fas fa-info-circle"></i>
                      <p>
                        This {getCategoryTitle().toLowerCase()} is assigned to <strong>{selectedItem.employee_count}</strong> employee(s).
                        <br />
                        Their {getCategoryTitle().toLowerCase()} will be set to "Not Assigned".
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <form className="modal-form">
                  {activeTab === 'units' && (
                    <div className="form-group">
                      <label htmlFor="code">Code <span className="required">*</span></label>
                      <input
                        type="text"
                        id="code"
                        className="form-input"
                        value={formData.code || ''}
                        onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                        placeholder="Enter unit code (e.g., MT, KG)"
                        required
                        readOnly={modalMode === 'edit'}
                        style={modalMode === 'edit' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                      {modalMode === 'edit' && (
                        <small style={{ color: '#666', fontSize: '12px' }}>Code cannot be changed after creation</small>
                      )}
                    </div>
                  )}

                  {activeTab === 'branches' && (
                    <div className="form-group">
                      <label htmlFor="branch-code">Code <span className="required">*</span></label>
                      <select
                        id="branch-code"
                        className="form-input"
                        value={formData.code || ''}
                        onChange={(e) => handleInputChange('code', e.target.value)}
                        disabled={modalMode === 'edit'}
                        required
                        style={modalMode === 'edit' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      >
                        <option value="">Select a branch code...</option>
                        {BRANCH_CODE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {modalMode === 'edit' && (
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          Code cannot be changed — historical enquiries reference it via FK
                        </small>
                      )}
                    </div>
                  )}

                  {activeTab !== 'ports' && (
                    <div className="form-group">
                      <label htmlFor="name">Name <span className="required">*</span></label>
                      <input
                        type="text"
                        id="name"
                        className="form-input"
                        value={formData.name || ''}
                        onChange={(e) => {
                          const newName = e.target.value;
                          handleInputChange('name', newName);
                          if (activeTab === 'offices' && crmProfiles.length > 0 && !formData.linked_crm_profile_id) {
                            const match = crmProfiles.find(p =>
                              p.legal_entity_name.toLowerCase() === newName.trim().toLowerCase()
                            );
                            if (match) {
                              handleInputChange('linked_crm_profile_id', match.id);
                            }
                          }
                        }}
                        placeholder={`Enter ${getCategoryTitle().toLowerCase()} name`}
                        required
                      />
                    </div>
                  )}

                  {activeTab === 'ports' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="port-unlocode">UN/LOCODE <span className="required">*</span></label>
                        <input
                          type="text"
                          id="port-unlocode"
                          className="form-input"
                          value={formData.unlocode || ''}
                          onChange={(e) => handleInputChange('unlocode', e.target.value.toUpperCase())}
                          placeholder="e.g. SGSIN"
                          maxLength={10}
                          required
                          readOnly={modalMode === 'edit'}
                          style={modalMode === 'edit' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                        />
                        {modalMode === 'edit' && (
                          <small style={{ color: '#666', fontSize: '12px' }}>
                            UN/LOCODE cannot be changed after creation. Delete and recreate to change it.
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="port-name">Port Name <span className="required">*</span></label>
                        <input
                          type="text"
                          id="port-name"
                          className="form-input"
                          value={formData.port_name || ''}
                          onChange={(e) => handleInputChange('port_name', e.target.value)}
                          placeholder="e.g. Port of Singapore"
                          maxLength={100}
                          required
                        />
                      </div>

                      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                        <div>
                          <label htmlFor="port-country">Country Name <span className="required">*</span></label>
                          <input
                            type="text"
                            id="port-country"
                            className="form-input"
                            value={formData.country_name || ''}
                            onChange={(e) => handleInputChange('country_name', e.target.value)}
                            placeholder="e.g. Singapore"
                            maxLength={100}
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="port-iso">ISO Code <span className="required">*</span></label>
                          <input
                            type="text"
                            id="port-iso"
                            className="form-input"
                            value={formData.country_iso_code || ''}
                            onChange={(e) => handleInputChange('country_iso_code', e.target.value.toUpperCase())}
                            placeholder="SG"
                            maxLength={10}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label htmlFor="port-lat">Latitude</label>
                          <input
                            type="number"
                            id="port-lat"
                            className="form-input"
                            value={formData.latitude ?? ''}
                            onChange={(e) => handleInputChange('latitude', e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="-90 to 90"
                            step="any"
                            min={-90}
                            max={90}
                          />
                        </div>
                        <div>
                          <label htmlFor="port-lng">Longitude</label>
                          <input
                            type="number"
                            id="port-lng"
                            className="form-input"
                            value={formData.longitude ?? ''}
                            onChange={(e) => handleInputChange('longitude', e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="-180 to 180"
                            step="any"
                            min={-180}
                            max={180}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="crm-input-label">Module lists</label>
                        <div className="crm-checkbox-group">
                          <div className="crm-checkbox-item">
                            <input
                              type="checkbox"
                              id="port-is-bunker"
                              className="crm-checkbox"
                              checked={!!formData.is_bunker_port}
                              onChange={(e) => handleInputChange('is_bunker_port', e.target.checked)}
                            />
                            <label htmlFor="port-is-bunker" className="crm-checkbox-label">
                              Bunker-eligible (port appears in the Fuels enquiry port picker)
                            </label>
                          </div>
                          <div className="crm-checkbox-item">
                            <input
                              type="checkbox"
                              id="port-is-cargo"
                              className="crm-checkbox"
                              checked={!!formData.is_cargo_port}
                              onChange={(e) => handleInputChange('is_cargo_port', e.target.checked)}
                            />
                            <label htmlFor="port-is-cargo" className="crm-checkbox-label">
                              Cargo-eligible (port appears in the Cargo recap/freight port pickers)
                            </label>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'offices' && (
                    <div className="form-group">
                      <label htmlFor="office-hms-entity">IMT Entity (Financials)</label>
                      <select
                        id="office-hms-entity"
                        className="form-input"
                        value={formData.hms_entity_id || ''}
                        onChange={(e) => handleInputChange('hms_entity_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                        disabled={hmsEntitiesLoadFailed}
                      >
                        {hmsEntitiesLoadFailed ? (
                          // Keeps the saved link untouched: the select can't be changed, and
                          // it never claims "Not linked" for an office that is linked.
                          <option value={formData.hms_entity_id || ''}>
                            {formData.hms_entity_id
                              ? (formData.hms_entity_name || 'Linked (company list unavailable)')
                              : '-- Company list unavailable --'}
                          </option>
                        ) : (
                          <>
                            <option value="">-- Not linked --</option>
                            {hmsEntities.map(en => (
                              <option key={en.id} value={en.id}>{en.name}</option>
                            ))}
                          </>
                        )}
                      </select>
                      {hmsEntitiesLoadFailed && (
                        <div>
                          <ListLoadFailed what="the list of IMT companies" onRetry={loadHmsEntitiesForLinking} />
                        </div>
                      )}
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Whose books this office keeps. Staff here see only this entity&apos;s
                        invoices, GL accounts, cost centres, VAT rates and bank statements.
                        Leave it unset and they are refused Financials with a message saying why.
                      </small>
                    </div>
                  )}

                  {activeTab === 'offices' && (
                    <div className="form-group">
                      <label htmlFor="linked-crm-profile">Linked CRM Profile</label>
                      <select
                        id="linked-crm-profile"
                        className="form-input"
                        value={formData.linked_crm_profile_id || ''}
                        onChange={(e) => handleInputChange('linked_crm_profile_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                      >
                        <option value="">-- None --</option>
                        {crmProfiles.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.legal_entity_name}{p.is_group_entity ? ' (IMT Entity)' : ''}
                          </option>
                        ))}
                      </select>
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Link this office to a CRM profile to enable HR contact import
                      </small>
                    </div>
                  )}

                  {activeTab === 'units' && (
                    <div className="form-group">
                      <label htmlFor="description">Description</label>
                      <input
                        type="text"
                        id="description"
                        className="form-input"
                        value={formData.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Enter unit description (e.g., MT (Metric Ton))"
                      />
                    </div>
                  )}

                  {activeTab === 'positions' && (
                    <div className="form-group">
                      <label className="crm-input-label">Verification Required</label>
                      <div className="crm-checkbox-group">
                        <div className="crm-checkbox-item">
                          <input
                            type="checkbox"
                            id="requires-verification-checkbox"
                            className="crm-checkbox"
                            checked={formData.requires_verification || false}
                            onChange={(e) => handleInputChange('requires_verification', e.target.checked)}
                          />
                          <label htmlFor="requires-verification-checkbox" className="crm-checkbox-label">
                            Requires admin verification when assigned
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'branches' && (
                    <div className="form-group">
                      <label className="crm-input-label">Active</label>
                      <div className="crm-checkbox-group">
                        <div className="crm-checkbox-item">
                          <input
                            type="checkbox"
                            id="branch-is-active-checkbox"
                            className="crm-checkbox"
                            checked={
                              modalMode === 'create'
                                ? (formData.is_active !== false)
                                : !!formData.is_active
                            }
                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                          />
                          <label htmlFor="branch-is-active-checkbox" className="crm-checkbox-label">
                            Branch is active (inactive branches don't appear in the Fuels enquiry branch picker)
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className={`btn ${modalMode === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                onClick={handleSubmit}
                disabled={
                  submitting
                  || (modalMode !== 'delete' && activeTab !== 'ports' && !formData.name)
                  || (modalMode !== 'delete' && activeTab === 'ports' && (
                    !formData.port_name || !formData.country_name || !formData.country_iso_code
                    || (modalMode === 'create' && !formData.unlocode)
                  ))
                  || (modalMode === 'create' && activeTab === 'branches' && !formData.code)
                }
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>
                    {modalMode === 'create' && 'Create'}
                    {modalMode === 'edit' && 'Save Changes'}
                    {modalMode === 'delete' && 'Delete'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VAT Rates Modal */}
      {showVatModal && (
        <div className="admin-modal-overlay" onClick={handleVatModalClose}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>VAT Rates {vatEntityName ? `- ${vatEntityName}` : ''}</h2>
              <button className="close-btn" onClick={handleVatModalClose}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              {vatError && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {vatError}
                </div>
              )}

              {vatLoading ? (
                <div className="loading">Loading VAT rates...</div>
              ) : !vatError && vatRates.length > 0 ? (
                <div className="vat-rates-list">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }}>
                      Select which VAT rates are available when creating invoices for this entity.
                    </p>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', whiteSpace: 'nowrap' }}
                      onClick={() => setShowAddVatForm(!showAddVatForm)}
                    >
                      {showAddVatForm ? 'Cancel' : '+ Add Rate'}
                    </button>
                  </div>

                  {showAddVatForm && (
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.8rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#374151', display: 'block', marginBottom: '0.2rem' }}>Code *</label>
                          <input
                            type="text"
                            className="form-input"
                            style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                            placeholder="e.g. VAT_STD_21"
                            value={newVatData.vat_code}
                            onChange={(e) => setNewVatData(prev => ({ ...prev, vat_code: e.target.value.toUpperCase() }))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#374151', display: 'block', marginBottom: '0.2rem' }}>Description *</label>
                          <input
                            type="text"
                            className="form-input"
                            style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                            placeholder="e.g. Standard VAT Rate"
                            value={newVatData.description}
                            onChange={(e) => setNewVatData(prev => ({ ...prev, description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#374151', display: 'block', marginBottom: '0.2rem' }}>Rate % *</label>
                          <AmountInput
                            className="form-input"
                            style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                            maxDecimals={2}
                            placeholder="e.g. 21"
                            value={newVatData.rate_percentage}
                            onChange={(v) => setNewVatData(prev => ({ ...prev, rate_percentage: v }))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#374151', display: 'block', marginBottom: '0.2rem' }}>Country</label>
                          <input
                            type="text"
                            className="form-input"
                            style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                            placeholder="e.g. DE"
                            maxLength={10}
                            value={newVatData.country_code}
                            onChange={(e) => setNewVatData(prev => ({ ...prev, country_code: e.target.value.toUpperCase() }))}
                          />
                        </div>
                      </div>
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                        disabled={vatCreating || !newVatData.vat_code || !newVatData.description || !newVatData.rate_percentage}
                        onClick={handleCreateVatRate}
                      >
                        {vatCreating ? 'Creating...' : 'Create & Assign'}
                      </button>
                    </div>
                  )}

                  <table className="employees-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Code</th>
                        <th>Rate</th>
                        <th>Description</th>
                        <th>Country</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vatRates.map(rate => (
                        <tr
                          key={rate.id}
                          onClick={() => handleVatToggle(rate.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={rate.is_allowed}
                              onChange={() => handleVatToggle(rate.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td>{rate.vat_code}</td>
                          <td>{rate.rate_percentage}%</td>
                          <td>{rate.description}</td>
                          <td>{rate.country_code || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !vatError ? (
                <p>No VAT rates available.</p>
              ) : null}
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleVatModalClose}
                disabled={vatSaving}
              >
                Cancel
              </button>
              {!vatError && vatRates.length > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleVatSave}
                  disabled={vatSaving || vatLoading}
                >
                  {vatSaving ? (
                    <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Branch Staffing Modal (Sprint 1F) */}
      {showStaffingModal && staffingBranch && (
        <BranchStaffingModal
          branch={staffingBranch}
          onClose={handleStaffingModalClose}
          onSaved={handleStaffingSaved}
        />
      )}

      {/* Port Bulk Upload Modal */}
      {showPortBulkUpload && (
        <PortBulkUploadModal
          onClose={handlePortBulkUploadClose}
          onUploaded={handlePortBulkUploaded}
        />
      )}
    </div>
  );
};

export default SystemDataManagement;
